import {QUARTER_ZONE_BURN} from '../../configs/buildingConstructionAnimation';
import {mixColor} from './buildAbilityCast';

/**
 * Поза прожигаемой ячейки зоны кварталов во времени (см. scenes/QuarterZoneBurn).
 * Только расчет: elapsedMs — от вспышки этой ячейки, до нее все погашено.
 */

const TWO_PI = Math.PI * 2;
const clamp01 = (value) => Math.min(1, Math.max(0, value));
const fadeBetween = (elapsedMs, fromMs, toMs) => 1 - clamp01((elapsedMs - fromMs) / (toMs - fromMs));

/** Цвет остывания: по ступеням от белого до темно-красного */
export const resolveZoneBurnColor = (elapsedMs) => {
  const {ramp} = QUARTER_ZONE_BURN;
  const nextIndex = ramp.findIndex(({atMs}) => atMs > elapsedMs);
  if (nextIndex === 0) {
    return ramp[0].color;
  }
  if (nextIndex < 0) {
    return ramp[ramp.length - 1].color;
  }
  const from = ramp[nextIndex - 1];
  const to = ramp[nextIndex];
  return mixColor(from.color, to.color, (elapsedMs - from.atMs) / (to.atMs - from.atMs));
};

/**
 * @param {number} elapsedMs
 * @returns {{color: number, heatAlpha: number, outlineAlpha: number, hatchAlpha: number, baseShare: number}}
 *   baseShare — доля проявления обычной штриховки
 */
export const resolveZoneBurnPose = (elapsedMs) => {
  const {igniteMs, heat, outline, hatch, base} = QUARTER_ZONE_BURN;
  if (elapsedMs < 0) {
    return {color: resolveZoneBurnColor(0), heatAlpha: 0, outlineAlpha: 0, hatchAlpha: 0, baseShare: 0};
  }
  const ignite = clamp01(elapsedMs / igniteMs);
  return {
    color: resolveZoneBurnColor(elapsedMs),
    heatAlpha: heat.alpha * ignite * fadeBetween(elapsedMs, heat.fadeFromMs, heat.endMs),
    outlineAlpha: outline.alpha * ignite * fadeBetween(elapsedMs, igniteMs, outline.endMs),
    hatchAlpha: ignite * fadeBetween(elapsedMs, hatch.holdMs, hatch.endMs),
    baseShare: clamp01((elapsedMs - base.startMs) / (base.endMs - base.startMs)),
  };
};

/**
 * Уголек над ячейкой: поднимается, покачиваясь, и гаснет
 *
 * @param {number} elapsedMs — от вспышки ячейки
 * @param {number} index — номер уголька
 * @param {number} seed — случайное число ячейки от 0 до 1 (только вид)
 * @returns {{offsetX: number, offsetY: number, scale: number, alpha: number}} смещение — в размерах ячейки
 */
export const resolveEmberPose = (elapsedMs, index, seed) => {
  const {startMs, lifeMs, staggerMs, spreadFactor, riseFactor} = QUARTER_ZONE_BURN.embers;
  const local = (elapsedMs - startMs - index * staggerMs) / lifeMs;
  const phase = (seed + index * 0.381966) % 1;
  const angle = phase * TWO_PI;
  const radius = spreadFactor * Math.sqrt((phase * 7.13) % 1);
  const startX = Math.cos(angle) * radius;
  const startY = Math.sin(angle) * radius * 0.8;
  if (local <= 0 || local >= 1) {
    return {offsetX: startX, offsetY: startY, scale: 0, alpha: 0};
  }
  return {
    offsetX: startX + Math.sin(local * TWO_PI + angle) * 0.08,
    offsetY: startY - riseFactor * (1 - (1 - local) ** 2),
    scale: 1 - local * 0.55,
    alpha: Math.sin(local * Math.PI) ** 0.7,
  };
};
